import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Divider, Link, Pill, SkeletonBase, Text } from "@foris/avocado-suite";
import { AssignmentProposalDimension } from "@models/ISchema";
import { formatProposalChangeHeading, formatProposalChangeLine } from "../../utils/proposalCard";
import type { ProposalCardModel, ProposalCardStatusTone } from "../../utils/proposalCard";
import styles from "./proposalCard.module.scss";

interface ProposalCardProps {
  proposal: ProposalCardModel;
  /** Opens the editor for this proposal. */
  onAction: () => void;
  /** Review cards the user may reject: opens the rejection modal. */
  onReject?: () => void;
  /** Drafts the user may delete: opens the delete confirmation. */
  onDelete?: () => void;
}

const DIMENSION_ICON = {
  [AssignmentProposalDimension.Schedule]: "clock",
  [AssignmentProposalDimension.Weeks]: "calendar",
  [AssignmentProposalDimension.Classrooms]: "building",
  [AssignmentProposalDimension.Instructors]: "user",
} as const;

/** State pills as in the Figma of the Drawer: a draft is outlined, the rest a 10% tint. */
const STATUS_PILL = {
  draft: { color: "neutral-10", textColor: "neutral-80", outlined: true },
  pending: { color: "warning-opacity", textColor: "warning-60", outlined: false },
  approved: { color: "info-opacity", textColor: "info-60", outlined: false },
  rejected: { color: "error-opacity", textColor: "error-60", outlined: false },
} as const satisfies Record<
  ProposalCardStatusTone,
  { color: string; textColor: string; outlined: boolean }
>;

/** Placeholder of a card while the proposals are being requested. */
export const ProposalCardSkeleton = () => (
  <div className={styles.proposalCard} aria-hidden="true">
    <SkeletonBase height={20} width="60%" radius={4} />
    <SkeletonBase height={24} width="45%" radius={12} />
    <SkeletonBase height={16} width="100%" radius={4} />
  </div>
);

/**
 * A proposal in the proposals Drawer: its state, what it changes, who sent it and when, and the
 * actions that open it in the assignation editor, reject it or delete it. It only renders what the
 * model already decided.
 */
const ProposalCard = ({ proposal, onAction, onReject, onDelete }: ProposalCardProps) => {
  const { t } = useTranslation();
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const detailId = useId();
  const hasDetail = proposal.changeGroups.length > 0;

  return (
    <article className={styles.proposalCard}>
      <header className={styles.proposalCard_header}>
        <Text className={styles.proposalCard_title} type="md" weight="medium">
          {t("group-proposals.card.title")}
        </Text>

        {!!proposal.statusKey && !!proposal.statusTone && (
          <Pill className={styles.proposalCard_status} {...STATUS_PILL[proposal.statusTone]}>
            {t(`group-proposals.status.${proposal.statusKey}`)}
          </Pill>
        )}
      </header>

      {!proposal.hasSummary && (
        <Text className={styles.proposalCard_noSummary} type="sm">
          {t("group-proposals.card.no-summary")}
        </Text>
      )}

      {proposal.dimensions.length > 0 && (
        <ul className={styles.proposalCard_dimensions}>
          {proposal.dimensions.map(dimension => (
            <li key={dimension}>
              <Pill
                className={styles.proposalCard_dimension}
                icon={DIMENSION_ICON[dimension]}
                iconColor="primary-60"
              >
                {t(`group-proposals.dimensions.${dimension}`)}
              </Pill>
            </li>
          ))}
        </ul>
      )}

      {hasDetail && (
        <>
          <Link
            className={styles.proposalCard_detailToggle}
            size="sm"
            iconRight={isDetailOpen ? "chevron-up" : "chevron-down"}
            aria-expanded={isDetailOpen}
            aria-controls={detailId}
            onClick={() => setIsDetailOpen(isOpen => !isOpen)}
          >
            {t("group-proposals.card.detail")}
          </Link>

          {isDetailOpen && (
            <div id={detailId} className={styles.proposalCard_detail}>
              {proposal.changeGroups.map((group, groupIndex) => (
                <div key={groupIndex} className={styles.proposalCard_changeGroup}>
                  {!!group.heading && (
                    <Text type="sm" weight="medium">
                      {formatProposalChangeHeading(group.heading, t)}
                    </Text>
                  )}

                  <ul className={styles.proposalCard_changes}>
                    {group.lines.map((line, lineIndex) => (
                      <li key={lineIndex}>
                        <Text type="sm">{formatProposalChangeLine(line, t)}</Text>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {!!proposal.reviewerName && (
        <Text type="sm">{t("group-proposals.card.reviewer", { name: proposal.reviewerName })}</Text>
      )}

      {!!proposal.rejectionReason && (
        <Text className={styles.proposalCard_rejectionReason} type="sm">
          {t("group-proposals.card.rejection-reason", { reason: proposal.rejectionReason })}
        </Text>
      )}

      {/* The divider of the Figma: avocado's lighter variant (neutral-10). */}
      <Divider disabled />

      <footer className={styles.proposalCard_footer}>
        {/* When it was created and, for a sent proposal, by whom. */}
        {!!proposal.createdOn && (
          <Text className={styles.proposalCard_created} type="xs">
            {proposal.creatorName
              ? t("group-proposals.card.created-by", {
                  date: proposal.createdOn,
                  name: proposal.creatorName,
                })
              : t("group-proposals.card.created-on", { date: proposal.createdOn })}
          </Text>
        )}

        <div className={styles.proposalCard_actions}>
          {proposal.canDelete && !!onDelete && (
            <Button variant="ghost" size="sm" leftIcon="trash" onClick={onDelete}>
              {t("group-proposals.actions.delete")}
            </Button>
          )}

          {proposal.canReject && !!onReject && (
            <Button variant="ghost" size="sm" leftIcon="close" onClick={onReject}>
              {t("group-proposals.actions.reject")}
            </Button>
          )}

          {!!proposal.action && (
            <Button variant="ghost" size="sm" onClick={onAction}>
              {t(`group-proposals.actions.${proposal.action}`)}
            </Button>
          )}
        </div>
      </footer>
    </article>
  );
};

export default ProposalCard;
